import type * as React from 'react'

import { clsx } from 'clsx'

import styles from './PostCardView.module.css'

import type { JSX } from 'react'

export type PostCardViewProps = {
  /** Omit to drop the header row entirely rather than render it empty. */
  header?: React.ReactNode
  banner?: React.ReactNode
  content: React.ReactNode
  /**
   * Omit (undefined) — not a null-rendering node — to drop the media column,
   * since the wrapper renders whenever this prop is defined.
   */
  media?: React.ReactNode
  meta?: React.ReactNode
  metrics?: React.ReactNode
  footer?: React.ReactNode
  className?: string
}

/**
 * Presentational layout for a post/draft details card. Pure visual shell: it
 * knows nothing about posts, channels, or GraphQL — callers pass rendered
 * slots. Post and ContentItem (channel-less draft) details cards both feed it,
 * so the layout stays a single source of truth while each entity keeps its own
 * data and handlers.
 */
export function PostCardView({
  header,
  banner,
  content,
  media,
  meta,
  metrics,
  footer,
  className,
}: PostCardViewProps): JSX.Element {
  return (
    <article className={clsx(styles.card, className)}>
      {banner}
      {header}
      <section className={styles.body}>
        <div className={styles.bodyWrapper}>
          <div className={styles.content}>{content}</div>
          {media != null && (
            <div className={styles.media} data-testid="post-card-view-media">
              {media}
            </div>
          )}
        </div>
        {meta}
      </section>
      {metrics}
      {footer}
    </article>
  )
}
